import { Field } from '@/components/ui';
import type { ReactNode } from 'react';

import type { CommonState } from './infoTabFields';
import styles from './InfoTab.module.css';

interface CommonInfoCardProps {
  // 5자리 game ID. 신규 프로젝트는 아직 미부여라 빈 문자열 — autoAssign으로 "자동 부여" 표시.
  projectId: string;
  stageEditor?: ReactNode;
  common: CommonState;
  // form 초기화 트리거용 key 버전 — 부모에서 reset/submit 시 증가.
  ver: number;
  onChangeField: <K extends keyof CommonState>(key: K, value: CommonState[K]) => void;
  // 읽기전용 표시 모드(요청 상세에서 입력값 확인용) — 잠금/수정 버튼 없이 값만 노출.
  readonly?: boolean;
  // 요청 상세에서 이번 요청으로 바뀌는 칸의 이전 값(화면 표기).
  changedFrom?: Partial<Record<keyof CommonState, string>>;
  // 요청 상세 '바뀐 칸만' — changedFrom 에 있는 칸만 그린다(Stage 는 stageEditor 로 부모가 정함).
  onlyChanged?: boolean;
}

// 공통 정보 카드 — 모든 OS에 공통으로 적용되는 메타 정보 입력 폼.
// 프로젝트 수정(InfoTab) · 새 프로젝트 요청(ProjectNew) · 요청 상세(읽기전용)가 공유.
export default function CommonInfoCard({
  projectId,
  stageEditor,
  common,
  ver,
  onChangeField,
  readonly = false,
  changedFrom,
  onlyChanged = false,
}: CommonInfoCardProps) {
  const show = (key: keyof CommonState) => !onlyChanged || (changedFrom !== undefined && key in changedFrom);
  return (
    <section className={styles.card} aria-label="공통 정보">
      <div>
        <div className={styles.section}>공통 정보</div>
        <div className={styles.sectionSub}>모든 OS에 공통으로 적용되는 메타 정보</div>
      </div>
      <div className={`${styles.grid} ${stageEditor ? styles.stageGrid : ''}`} style={{ marginTop: 16 }}>
        {!onlyChanged && <Field
          key={`pid-${ver}`}
          label="project_id"
          mono
          readonly
          autoAssign
          value={projectId || '자동입력'}
          help="5자리 숫자 game ID (utils.apps.project_id, 승인 시 자동 부여)"
        />}
        {stageEditor}
        {show('display_name') && <Field
          key={`dn-${ver}`}
          lockable
          readonly={readonly}
          label="display_name"
          mono
          required
          value={common.display_name}
          onChange={(v) => onChangeField('display_name', v)}
          help="홈/카드에 노출되는 영문 이름"
          changedFrom={changedFrom?.display_name}
        />}
        {show('app_match_name') && <Field
          key={`am-${ver}`}
          lockable
          readonly={readonly}
          label="app_match_name"
          mono
          required
          value={common.app_match_name}
          onChange={(v) => onChangeField('app_match_name', v)}
          help="내부 매칭용 소문자 키"
          changedFrom={changedFrom?.app_match_name}
        />}
        {/* bundle_id / ga_game_id / tapjoy_app_id는 플랫폼(aos/ios)별로 값이 달라 '플랫폼별 정보'에서 편집한다. */}
        {show('gcp_project_id') && <Field
          key={`gcp-${ver}`}
          lockable
          readonly={readonly}
          label="gcp_project_id"
          mono
          value={common.gcp_project_id}
          onChange={(v) => onChangeField('gcp_project_id', v)}
          help="해당 앱이 사용하는 GCP 프로젝트 ID"
          changedFrom={changedFrom?.gcp_project_id}
        />}
        {/* adjust_app_token은 프로젝트당 1개(모든 OS 공통)라 공통 정보에서 편집한다. */}
        {show('adjust_app_token') && <Field
          key={`adj-${ver}`}
          lockable
          readonly={readonly}
          label="adjust_app_token"
          mono
          value={common.adjust_app_token}
          onChange={(v) => onChangeField('adjust_app_token', v)}
          help="adjust에서 사용되는 app token입니다."
          changedFrom={changedFrom?.adjust_app_token}
        />}
        {show('publisher') && <Field
          key={`pub-${ver}`}
          lockable
          readonly={readonly}
          label="publisher"
          mono
          value={common.publisher}
          onChange={(v) => onChangeField('publisher', v)}
          help="퍼블리셔 (기본값 supercent)"
          changedFrom={changedFrom?.publisher}
        />}
        {show('company') && <Field
          key={`co-${ver}`}
          lockable
          readonly={readonly}
          label="company"
          mono
          value={common.company}
          onChange={(v) => onChangeField('company', v)}
          help="회사 (기본값 supercent)"
          changedFrom={changedFrom?.company}
        />}
      </div>
    </section>
  );
}
